import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { countries } from '../data/countries';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiLock, FiX, FiCheckCircle, FiInfo } from 'react-icons/fi';
import { cn } from '../utils/cn';

export default function IdentityModal({ isOpen, onClose, onCancel, onConfirm, usedOnMapCountries = [] }) {
  const { lockedCountries, user } = useAuth();
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const filtered = countries.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const isFull = lockedCountries.length >= 2;

  const isUsedOnMap = (code) => usedOnMapCountries.includes(code);

  const handleSelect = (country) => {
    if (isFull && !lockedCountries.includes(country.code)) return;
    if (isUsedOnMap(country.code)) return;
    setSelected(country);
  };

  const handleConfirm = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onConfirm(selected.code);
      if (ok !== false) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-xl font-black uppercase italic tracking-tighter flex items-center gap-2">
            <SafeIcon icon={FiLock} className="text-lime-400" />
            {t('select_identity')}
          </h2>
          <button type="button" onClick={onCancel} className="text-slate-500 hover:text-white transition-colors">
            <SafeIcon icon={FiX} size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-sm text-slate-400">
            {isFull ? t('identity_locked_desc') : t('identity_new_desc')}
          </p>

          {lockedCountries.length > 0 && (
            <div className="flex gap-2">
              {lockedCountries.map(code => {
                const c = countries.find(x => x.code === code);
                const usedOnMap = isUsedOnMap(code);
                return (
                  <button
                    key={code}
                    disabled={usedOnMap}
                    onClick={() => handleSelect(c)}
                    className={cn(
                      "flex-1 p-3 rounded-lg border font-bold text-sm transition-all flex items-center justify-between",
                      usedOnMap && "opacity-40 cursor-not-allowed bg-slate-950 border-slate-800 text-slate-600",
                      !usedOnMap && selected?.code === code && "bg-lime-400/20 border-lime-400 text-lime-400",
                      !usedOnMap && selected?.code !== code && "bg-slate-800 border-slate-700 text-slate-300"
                    )}
                  >
                    <span>{c?.name}{usedOnMap ? ' ✓' : ''}</span>
                    <SafeIcon icon={FiCheckCircle} className={selected?.code === code ? "opacity-100" : "opacity-0"} />
                  </button>
                );
              })}
            </div>
          )}

          {!isFull && (
            <div className="space-y-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder={t('search_countries')}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-10 pr-4 text-sm outline-none focus:border-lime-400 transition-colors"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="max-h-48 overflow-y-auto custom-scrollbar border border-slate-800 rounded-lg">
                {filtered.map(c => {
                  const isLockedOther = lockedCountries.length > 0 && lockedCountries.includes(c.code);
                  return (
                    <button
                      key={c.code}
                      disabled={isLockedOther}
                      onClick={() => handleSelect(c)}
                      className={cn(
                        "w-full text-left p-3 text-sm transition-colors flex items-center justify-between",
                        selected?.code === c.code ? "bg-lime-400/10 text-lime-400" : "hover:bg-slate-800 text-slate-400",
                        isLockedOther && "opacity-30 cursor-not-allowed"
                      )}
                    >
                      <span>{c.name}</span>
                      {isLockedOther && <SafeIcon icon={FiLock} size={12} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isFull && !user && (
            <div className="bg-blue-500/10 border border-blue-500/20 p-3 rounded flex gap-3">
              <SafeIcon icon={FiInfo} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-300 leading-relaxed">
                {t('change_identity_msg')}
              </p>
            </div>
          )}

          <button
            disabled={!selected || submitting}
            onClick={handleConfirm}
            className="w-full py-4 bg-lime-400 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-black uppercase tracking-widest rounded-xl transition-all hover:scale-[1.02]"
          >
            {submitting ? t('loading') : t('confirm_identity')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
